// src/pages/profile/ProfileScreen.tsx
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import {
  MdHistory,
  MdLocationOn,
  MdFavorite,
  MdAccountBalanceWallet,
  MdLocalShipping,
  MdLogout,
  MdChevronRight,
  MdCreditCard,
  MdPerson,
  MdPlaylistAdd,
  MdQrCode,
} from "react-icons/md";
import { useTheme } from "../../common/hooks/useTheme";
import { useLogout } from "../../common/hooks/useLogout";
import { useProfileScreen } from "./useProfileScreen";
import { selectIsOnline } from "../../store/slices/networkSlice";
import {
  selectIsExecutor,
  selectClientUserId,
  selectIsAdmin,
} from "../../store/slices/executorSlice";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import { MobileHeader } from "../../common/components/mobileHeader/MobileHeader";
import BottomModal from "../../common/components/bottomModal/BottomModal";
import { ServiceOtpContent } from "../../common/components/serviceOtpContent/ServiceOtpContent";
import styles from "./ProfileScreen.module.scss";
import { ROUTES } from "../../common/utils/constants";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { IconType } from "react-icons";

type RouteValue = (typeof ROUTES)[keyof typeof ROUTES];

interface MenuItem {
  icon: IconType;
  title: string;
  route: RouteValue;
}

export default function ProfileScreen() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { logOut } = useLogout();
  const {
    bonusBalance,
    bonusPercent,
    ordersCount,
    projectsCount,
    currentUser,
  } = useProfileScreen();
  const isOnline = useSelector(selectIsOnline);
  const currentCluster = useSelector(selectCurrentCluster);
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const clientUserId = useSelector(selectClientUserId);
  const [qrModalVisible, setQrModalVisible] = useState(false);

  const isYookassaEnabled = currentCluster?.yookassa?.enabled ?? false;
  const isBonusesEnabled = currentCluster?.withBonuses ?? false;
  const isDeliveryEnabled = currentCluster?.withDelivery ?? false;

  const displayName = useMemo(() => {
    if (clientUserId) return `Обслуживание клиента #${clientUserId}`;
    return currentUser?.email || null;
  }, [currentUser?.email, clientUserId]);

  const menuSections = useMemo(() => {
    const sections: { id: string; items: MenuItem[] }[] = [];

    if (currentCluster?.regularSales !== false) {
      sections.push({
        id: "orders",
        items: [
          { icon: MdHistory, title: "История заказов", route: ROUTES.ORDERS },
        ],
      });
    }

    if (currentCluster?.complexSales === true) {
      sections.push({
        id: "complexOrders",
        items: [
          {
            icon: MdPlaylistAdd,
            title: "Мои проекты",
            route: ROUTES.COMPLEX_ORDERS,
          },
        ],
      });
    }

    const userItems: MenuItem[] = [];

    // Пункты доступные только обычным пользователям (не админ и не исполнитель)
    const isRegularUser = !isExecutor && !isAdmin;

    if (isRegularUser) {
      userItems.push(
        { icon: MdLocationOn, title: "Мои адреса", route: ROUTES.ADDRESSES },
        { icon: MdFavorite, title: "Избранное", route: ROUTES.FAVORITES },
      );

      if (currentUser) {
        userItems.push({
          icon: MdQrCode,
          title: "QR-код",
          route: ROUTES.FAVORITES,
        });
      }

      if (isYookassaEnabled) {
        userItems.push({
          icon: MdCreditCard,
          title: "Способы оплаты",
          route: ROUTES.PAYMENT_METHODS,
        });
      }
    }

    if (userItems.length > 0) {
      sections.push({ id: "user", items: userItems });
    }

    if (isBonusesEnabled) {
      sections.push({
        id: "bonuses",
        items: [
          {
            icon: MdAccountBalanceWallet,
            title: "Бонусы",
            route: ROUTES.BONUS,
          },
          {
            icon: MdHistory,
            title: "История бонусов",
            route: ROUTES.BONUS_HISTORY,
          },
        ],
      });
    }

    if (isDeliveryEnabled) {
      sections.push({
        id: "info",
        items: [
          {
            icon: MdLocalShipping,
            title: "Условия доставки",
            route: ROUTES.DELIVERY,
          },
        ],
      });
    }

    return sections;
  }, [
    currentCluster,
    currentUser,
    isExecutor,
    isAdmin,
    isYookassaEnabled,
    isBonusesEnabled,
    isDeliveryEnabled,
  ]);

  const handleNavigation = (route: RouteValue) => navigate(route);

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <div className={styles.mobileOnly}>
        <MobileHeader
          title={String(displayName) || "Профиль"}
          rightIcon={<MdLogout size={24} color={theme.onPrimary} />}
          onRightClick={logOut}
          avatar={
            <div className={styles.mobileAvatarCircle}>
              <MdPerson size={40} color={theme.onPrimary} />
            </div>
          }
          stats={[
            { value: ordersCount, label: "Заказы" },
            ...(currentCluster?.complexSales === true
              ? [{ value: projectsCount, label: "Проекты" }]
              : []),
            ...(isBonusesEnabled
              ? [{ value: bonusBalance, label: `Баллы - ${bonusPercent}%` }]
              : []),
          ]}
        />
      </div>

      <div className={styles.desktopOnly}>
        <ProductHeader
          title="Профиль"
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
        <div className={styles.desktopUserInfo}>
          <div
            className={styles.desktopAvatarCircle}
            style={{ backgroundColor: theme.primary + "20" }}
          >
            <MdPerson size={32} color={theme.primary} />
          </div>
          {displayName && (
            <span className={styles.desktopUserName}>{displayName}</span>
          )}
        </div>
      </div>

      <div className={styles.content}>
        {!isOnline && (
          <div className={styles.offlineBadge} style={{ color: theme.warning }}>
            ⚡ Данные могут быть устаревшими
          </div>
        )}

        <div className={styles.sectionsContainer}>
          {menuSections.map((section) => (
            <div
              key={section.id}
              className={styles.section}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              {section.items.map((item, index) => (
                <button
                  key={index}
                  className={styles.menuItem}
                  onClick={() => {
                    if (item.title === "QR-код") {
                      setQrModalVisible(true);
                    } else {
                      handleNavigation(item.route);
                    }
                  }}
                >
                  <item.icon size={24} color={theme.primary} />
                  <span
                    className={styles.menuText}
                    style={{ color: theme.text }}
                  >
                    {item.title}
                  </span>
                  <MdChevronRight size={24} color={theme.textSecondary} />
                </button>
              ))}
            </div>
          ))}

          <div className={styles.desktopLogoutSection}>
            <div
              className={styles.section}
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <button className={styles.menuItem} onClick={logOut}>
                <MdLogout size={24} color={theme.danger} />
                <span className={styles.menuText} style={{ color: theme.text }}>
                  Выйти
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BottomModal
        isVisible={qrModalVisible}
        onClose={() => setQrModalVisible(false)}
      >
        <ServiceOtpContent />
      </BottomModal>
    </div>
  );
}
